import { colors, focusVisible } from "@quickpoint.me/shared";

import styled from "styled-components";
import { useEffect, useId, useRef, useState } from "react";

type TokenVariant = "keyword" | "modifier" | "control" | "value";

interface VocabTokenProps {
	label: string;
	help: string;
	onClick: () => void;
	variant: TokenVariant;
	preview?: React.ReactNode;
	isPortrait?: boolean;
}

const Tooltip = styled.div<{ $x: number; $y: number; $visible: boolean }>`
  position: fixed;
  left: ${(props) => props.$x}px;
  top: ${(props) => props.$y}px;
  transform: translate(-50%, -100%);
  padding: 6px 10px;
  background-color: ${colors.tufte.black};
  color: ${colors.white};
  font-family: ${(props) => props.theme.font};
  font-size: 0.8rem;
  font-weight: 400;
  border-radius: 4px;
  white-space: nowrap;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
  transition: opacity 0.15s ease;
  z-index: 9999;
  pointer-events: none;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  /* Arrow pointing down */
  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: ${colors.tufte.black};
  }
`;

const getVariantColor = (variant: TokenVariant) => {
	switch (variant) {
		case "keyword":
			return colors.textSafe.blue;
		case "modifier":
			return colors.textSafe.red;
		case "control":
			return colors.textSafe.magenta;
		case "value":
			return colors.textSafe.green;
		default:
			return colors.tufte.black;
	}
};

const getVariantBgTint = (variant: TokenVariant) => {
	switch (variant) {
		case "keyword":
			return `${colors.alabaster.active}12`;
		case "modifier":
			return `${colors.alabaster.red}12`;
		case "control":
			return `${colors.alabaster.magenta}12`;
		case "value":
			return `${colors.alabaster.green}12`;
		default:
			return "transparent";
	}
};

const StyledToken = styled.button<{
	$variant: TokenVariant;
	$isPortrait: boolean;
}>`
  display: flex;
  align-items: center;
  gap: ${(props) => (props.$isPortrait ? "0.5svw" : "0.3svw")};
  height: auto;
  min-height: 48px; /* 48px gold-standard target on both axes */
  min-width: 48px;
  /* explicit min-width removes the implicit min-content floor, so without
     this the overflowing row shrinks every token equally and the nowrap
     labels spill; keep full width and let the row scroll instead */
  flex-shrink: 0;
  padding: ${(props) => (props.$isPortrait ? "0.8svh 1.6svw" : "0.65svh 0.8svw")};
  border: 1.5px solid ${(props) => getVariantColor(props.$variant)}40;
  border-radius: 5px;
  background-color: ${(props) => getVariantBgTint(props.$variant)};
  font-family: ${(props) => props.theme.font};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  color: ${(props) => getVariantColor(props.$variant)};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  &:hover {
    background-color: ${(props) => getVariantColor(props.$variant)}20;
    border-color: ${(props) => getVariantColor(props.$variant)};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: scale(0.97);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  @media (prefers-reduced-motion: reduce) {
    &:active {
      transform: none;
    }
  }

  ${focusVisible}
`;

const PreviewContainer = styled.span`
  display: flex;
  align-items: center;
  margin-left: 0.2svw;
`;

function VocabToken({
	label,
	help,
	onClick,
	variant,
	preview,
	isPortrait = false,
}: VocabTokenProps) {
	const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0, visible: false });
	const tooltipId = useId();
	const tooltipRef = useRef<HTMLDivElement>(null);

	const showTooltip = (target: HTMLButtonElement) => {
		const rect = target.getBoundingClientRect();
		/* clamp so the (centered) tooltip never clips at the viewport edges */
		const halfWidth = (tooltipRef.current?.offsetWidth ?? 0) / 2;
		const pad = 8;
		const x = Math.min(
			Math.max(rect.left + rect.width / 2, halfWidth + pad),
			window.innerWidth - halfWidth - pad,
		);
		setTooltipPos({
			x,
			y: rect.top - 8,
			visible: true,
		});
	};

	const hideTooltip = () => {
		setTooltipPos((prev) => ({ ...prev, visible: false }));
	};

	// WCAG 1.4.13: content on hover/focus must be dismissable with Esc
	// without moving the pointer, so listen at the document level
	useEffect(() => {
		if (!tooltipPos.visible) return;
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") hideTooltip();
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [tooltipPos.visible]);

	return (
		<>
			<StyledToken
				$variant={variant}
				$isPortrait={isPortrait}
				onClick={onClick}
				onMouseEnter={(e) => showTooltip(e.currentTarget)}
				onMouseLeave={hideTooltip}
				onFocus={(e) => showTooltip(e.currentTarget)}
				onBlur={hideTooltip}
				type="button"
				aria-describedby={help ? tooltipId : undefined}
			>
				{label}
				{preview && <PreviewContainer>{preview}</PreviewContainer>}
			</StyledToken>
			{help && (
				<Tooltip
					ref={tooltipRef}
					id={tooltipId}
					role="tooltip"
					$x={tooltipPos.x}
					$y={tooltipPos.y}
					$visible={tooltipPos.visible}
				>
					{help}
				</Tooltip>
			)}
		</>
	);
}

export default VocabToken;
